import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import InfiniteScroll from "react-infinite-scroll-component";
import { useNavigate } from "react-router-dom";

import { Heading, HeadingWrapper, TeamFilter } from "../../styles";
import { List } from "./styles";
import { PageMenu } from "../../components/PageMenu";
import { ProjectItem } from "../../components/ProjectItem";
import { HeadingMenuContainer } from "../../components/NavBar/styles";
import { EmptyProjects } from "../../components/EmptyProjects";
import { setActiveModal } from "../../store/slices/modals";
import { requestAllProjects } from "../../store/slices/projects";
import { Loader } from "../../components/Loader";
import { getProjects, getProjectsSearch } from "../../store/selectors/projects";
import { LOCAL_STORAGE_KEYS } from "../../constants";
import { ProjectItemI } from "../../models/Projects";
import { SelectTeam } from "../../components/Inputs/CustomSelectTeam/Select";
import { getProfiles } from "../../store/selectors/profiles";
import { getTeams, getSelectedProjectTeam } from "../../store/selectors/teams";
import {
  requestAllTeams,
  setSelectedProjectTeam,
} from "../../store/slices/teams";
import { TeamAccessDenied } from "../../components/TeamAccessDenied";

export const Projects = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const allProjects = useSelector(getProjects);
  const profile = useSelector(getProfiles);
  const teams = useSelector(getTeams);
  const selectedTeam = useSelector(getSelectedProjectTeam);
  const [projects, setProjects] = useState<ProjectItemI[]>([]);
  const [refresh, setRefresh] = useState<number>(0);
  const search = useSelector(getProjectsSearch);
  const [skip, setSkip] = useState(0);
  const [limit, setLimit] = useState(10);
  const [hasMore, setHasMore] = useState(true);
  const [sortBy, setSortBy] = useState(
    localStorage.getItem(LOCAL_STORAGE_KEYS.sort)
      ? localStorage.getItem(LOCAL_STORAGE_KEYS.sort)
      : "createdAt"
  );
  const [orderBy, setOrderBy] = useState(
    localStorage.getItem(LOCAL_STORAGE_KEYS.order)
      ? localStorage.getItem(LOCAL_STORAGE_KEYS.order)
      : "DESC"
  );

  const handleSortChange = (sortOption: string) => {
    setSortBy(sortOption);
  };

  const openCreateProjectModal = (): any =>
    dispatch(setActiveModal({ id: "createProjectModal" }));

  const onSelectTeam = (e: any) => {
    dispatch(setSelectedProjectTeam(e.value));
  };

  useEffect(() => {
    if (
      profile?.id &&
      ((!profile?.firstName && !profile?.lastName) ||
        (profile?.firstName === profile?.email &&
          profile?.lastName === profile?.email))
    ) {
      navigate("/new-profile");
    }
    const storageTeam = window.localStorage.getItem("selectedTeam");
    if (storageTeam) {
      dispatch(setSelectedProjectTeam(storageTeam));
    }
    if (!teams?.length) {
      dispatch(requestAllTeams({}));
    }
    if (profile?.teams?.length && !selectedTeam && !storageTeam) {
      dispatch(setSelectedProjectTeam(profile?.teams[0]?.id));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profile]);

  useEffect(() => {
    if (!selectedTeam) {
      return;
    }
    dispatch(
      requestAllProjects({
        limit,
        skip,
        orderBy: sortBy!,
        orderDirection: orderBy!,
        name: search,
        teamId: selectedTeam,
        callbacks: {
          onSuccess: (data) => {
            setProjects(data.items);
            setHasMore(data.count > data.items?.length);
          },
        },
      })
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTeam]);

  useEffect(() => {
    if (!profile?.teams?.length || !selectedTeam) {
      return;
    }
    dispatch(
      requestAllProjects({
        limit,
        skip,
        orderBy: sortBy!,
        orderDirection: orderBy!,
        name: search,
        teamId: selectedTeam,
        callbacks: {
          onSuccess: (data) => {
            const newItems = [...projects, ...data.items];
            setProjects(newItems);
            setHasMore(data.count > newItems?.length);
          },
        },
      })
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, refresh]);

  const fetchData = () => {
    setLimit(10);
    setSkip(skip + 10);
    setRefresh(refresh + 1);
  };

  useEffect(() => {
    setProjects([]);
    setSkip(0);
    setLimit(10);
    setRefresh(refresh + 1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  useEffect(() => {
    setProjects([]);
    setLimit(skip);
    setSkip(0);
    setRefresh(refresh + 1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sortBy, orderBy]);

  const options = profile?.isAdmin ? teams?.map((team) => ({
    value: team.id,
    option: team.name,
  })) : teams?.filter((team) => profile?.teams?.find((t) => t?.id === team?.id))?.map((team) => ({
    value: team.id,
    option: team.name,
  }))

  if (profile?.id && !profile?.teams.length) {
    return <TeamAccessDenied />;
  }

  return (
    <>
      <HeadingMenuContainer>
        <HeadingWrapper>
          <Heading>Projects</Heading>
          <TeamFilter>
            Team
            {options?.length && selectedTeam && (
              <SelectTeam
                onSelect={onSelectTeam}
                options={options}
                value={selectedTeam || ""}
              />
            )}
          </TeamFilter>
        </HeadingWrapper>

        <PageMenu
          setOrderBy={setOrderBy}
          orderBy={orderBy}
          sortBy={sortBy}
          setSortBy={setSortBy}
          handleSortChange={handleSortChange}
        />
      </HeadingMenuContainer>
      {allProjects.isLoading ? <Loader fullScreen={true} /> : null}
      {projects.length === 0 && !allProjects.isLoading ? (
        <EmptyProjects openNewProjectModal={openCreateProjectModal} />
      ) : (
        <InfiniteScroll
          dataLength={projects.length}
          next={fetchData}
          hasMore={hasMore}
          loader={allProjects.isLoading && <h4>Loading...</h4>}
          endMessage={null}
        >
          <List>
            {projects?.map((project) => {
              return <ProjectItem project={project} key={project.id} />;
            })}
          </List>
        </InfiniteScroll>
      )}
    </>
  );
};
